import { useState, useEffect } from 'react'
import { m } from 'framer-motion'
import { Media, Section } from '@/components'
import { getTranslatedField } from '@/libs/utils'
import { useWindowSize } from '@/hooks'
import type { ArtworkType } from '@/@types/microcms-type'

type ProjectFirstViewProps = {
    project: Pick<ArtworkType, 'client' | 'project_title' | 'thumbnail_image'>
    locale?: string
}

export const ProjectFirstView = ({
    project: { client, project_title, thumbnail_image },
    locale
}: ProjectFirstViewProps) => {
    const isWideScreen = useWindowSize()
    const [isMounted, setIsMounted] = useState(false)
    const [variants, setVariants] = useState({})

    useEffect(() => {
        setIsMounted(true)

        setVariants({
            initial: {
                opacity: 0,
                y: isWideScreen ? '20px' : '0px',
                rotate: isWideScreen ? '0.4deg' : '0deg'
            },
            animate: {
                opacity: 1,
                y: '0px',
                rotate: '0deg',
                transition: { duration: 0.6, delay: 0.5 }
            }
        })
    }, [isWideScreen])

    return (
        <Section width="full" display="block">
            <header className="max-w-default lg:max-w-[70vw] mx-auto mb-16 md:mb-[4.3rem] md:text-center">
                {(client?.client_ja || client?.client_en) && (
                    <h2 className="text-v-md mb-2 md:mb-3 tracking-v-md translate-x-[2px]">
                        <m.span
                            key={client.fieldId}
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            transition={{ duration: 0.4, delay: 0.3 }}
                            className="inline-block"
                        >
                            {getTranslatedField({ field: client, locale })}
                        </m.span>
                    </h2>
                )}
                {(project_title.project_title_ja ||
                    project_title.project_title_en) && (
                    <h1 className="overflow-hidden text-[2.4rem] lg:text-[2.8rem]">
                        <m.span
                            key={project_title.fieldId}
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            transition={{ duration: 0.4, delay: 0.3 }}
                            className="inline-block"
                        >
                            {getTranslatedField({
                                field: project_title,
                                locale
                            })}
                        </m.span>
                    </h1>
                )}
            </header>

            {isMounted && (
                <Media
                    variants={variants}
                    initial="initial"
                    animate="animate"
                    media={thumbnail_image}
                    altText={getTranslatedField({
                        field: project_title,
                        locale
                    })}
                    quality={100}
                    priority
                    className="w-full h-auto md:h-[60vh] object-contain"
                />
            )}
        </Section>
    )
}
